import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

interface RangoDeEjeProps {
  /** Qué acota. No se dibuja: lo nombra la cabecera del gráfico, que es donde
   *  hay ancho para escribirlo junto a su lectura. Va al `aria-label`. */
  rotulo: string;
  orientacion: "horizontal" | "vertical";
  /** El valor MOSTRADO, que puede ser el que está en vuelo. Lo sostiene el
   *  gráfico con `useValorSostenido`, porque también dibuja la lectura. */
  valor: number[];
  /** Mueve la manija y la lectura, y nada más. */
  onArrastre: (valor: number[]) => void;
  /** El gesto terminó: recién acá se avisa hacia afuera. */
  onCambio: (valor: number[]) => void;
  min?: number;
  max: number;
  paso?: number;
  deshabilitado?: boolean;
  className?: string;
}

/**
 * El slider de un EJE del gráfico: acota mirando la nube, no desde la barra.
 *
 * Es el mismo control que `CampoDeRango` y a propósito NO lo reemplaza: aquel
 * vive en una barra de filtros, donde hay ancho para su rótulo, su lectura y
 * sus dos extremos. Acá el control se pega al eje —angosto y sin sitio para
 * texto— así que el rótulo y la lectura suben a la cabecera del gráfico y esto
 * queda siendo solo la pista.
 *
 * LA PISTA ES TODA LA ESCALA, no el tramo que se está viendo. Esa es la mitad
 * de lo que aporta: el gráfico hace zoom sobre la banda elegida y sin la pista
 * completa no quedaría nada diciendo cuánto del mercado quedó afuera. Se lee
 * como el minimapa de un visor — el relleno es la porción dibujada arriba.
 *
 * AVISA AL SOLTAR y nunca en cada movimiento, misma regla y mismo motivo que
 * `CampoDeRango`: cada aviso reescribe la url y cada url dispara dos consultas
 * más su preflight. Acá pesa todavía más, porque la nube que hay que redibujar
 * es todo el filtro y no las seis fichas de la página.
 */
export function RangoDeEje({
  rotulo,
  orientacion,
  valor,
  onArrastre,
  onCambio,
  min = 0,
  max,
  paso = 1,
  deshabilitado = false,
  className,
}: RangoDeEjeProps) {
  return (
    <Slider
      aria-label={rotulo}
      orientation={orientacion}
      value={valor}
      onValueChange={onArrastre}
      onValueCommit={onCambio}
      min={min}
      max={max}
      step={paso}
      disabled={deshabilitado}
      className={cn(
        // El mínimo de alto que trae el DS para la orientación vertical es más
        // que el área de trazado de este gráfico, así que se releva: acá el
        // alto no lo elige el control, lo dicta el eje al que se pega.
        orientacion === "vertical" && "min-h-0",
        className,
      )}
    />
  );
}
